import Vue from "vue";
import store from "@/store";
import i18n from "@/i18n";
import mApp from "@/modules/app";
import mDialog from "@/modules/dialog";
import mMenu from "@/modules/menu";
import mStore from "@/modules/store";
import mUtils from "@/modules/utils";

import router from "@/plugins/router";

import { ORDER_TYPE } from "@/modules/enums";
import { APP_MODE } from "@/modules/enums";

export default {
  /**
   * Initializes the order state from localStorage or sets default values
   * Clears order if it's older than 1 hour
   */
  initOrder() {
    let o_order = this.getOrderFromStorage();

    if (!o_order || Date.now() - o_order.lastUpdated > 60 * 60 * 1000) {
      this.clearOrder();

      store.state.Order.order.orderType = ORDER_TYPE.TAKEOUT;
      store.state.Order.order.tableID = 0;
      store.state.Order.order.tableName = "";
      store.state.Order.order.tableGroupID = 0;
      store.state.Order.order.tableGroupName = "";
      store.state.Order.order.phoneID = "";
      store.state.Order.order.memberCode = "";
      store.state.Order.order.memberVip = false;
      store.state.Order.order.login = null;
      store.state.Order.order.currency = store.getters.paymentCurrency;
      store.state.Order.order.defaultDiscountRate = 0;
      store.state.Order.order.defaultServiceValue = 0;
      store.state.Order.order.defaultServicePercentage = true;
      store.state.Order.order.minOrder = 0;
      store.state.Order.order.minOrderForFreeDelivery = 0;
      store.state.Order.order.priceLevel = 0;
      store.state.Order.order.payment = "skip";
      store.state.Order.order.orderNow = true;
      store.state.Order.order.utensils = store.getters.utensils;
    } else {
      store.state.Order.order = o_order;
      store.state.Order.order.lastUpdated = Date.now();
    }

    this.setOrderToStorage();
  },

  /**
   * Clears the current order from Vuex store and localStorage
   */
  clearOrder() {
    store.commit("ORDER_CLEAR");

    this.setOrderToStorage();
  },

  /**
   * Fetches orders for current member
   * @returns {Promise} Resolves with orders or empty array for non-members
   */
  getOrders() {
    let memberCode = store.getters.memberCode;

    if (memberCode == "") {
      return new Promise(function (resolve) {
        resolve({ result: "ok", orders: [] });
      });
    } else {
      let sessionKey = store.state.appSessionKey;
      if (Vue.prototype.$appMode == APP_MODE.WEB) sessionKey = store.state.Store.store.sessionKey;

      return mApp.apiPost(store.getters.doApiUrl + "/v2/getorders", {
        sessionKey: sessionKey,
        memberCode: memberCode,
      });
    }
  },

  /**
   * Sets order type and updates pricing/discounts accordingly
   * Reloads menu when order type or price level changes
   * @param {number} orderType - ORDER_TYPE enum value
   * @returns {Promise} Resolves when menu reload completes
   */
  setOrderType(orderType) {
    console.log("setOrderType", orderType);
    
    return new Promise(resolve => {
      let o_orderType = store.state.Order.order.orderType;
      let o_priceLevel = store.state.Order.order.priceLevel;

      if (orderType == null) orderType = store.state.Order.order.orderType;

      store.state.Order.order.orderType = orderType;
      try {
        let om = store.getters.storeOrderType(orderType);

        if (om) {
          if (om.distance) store.state.Order.order.distance = om.distance;
          else store.state.Order.order.distance = 0;

          if (om.discountRate) store.state.Order.order.defaultDiscountRate = om.discountRate;
          else store.state.Order.order.defaultDiscountRate = 0;

          if (om.serviceValue) store.state.Order.order.defaultServiceValue = om.serviceValue;
          else store.state.Order.order.defaultServiceValue = 0;

          store.state.Order.order.defaultServicePercentage = om.servicePercentage === true;

          if (om.payment) store.state.Order.order.payment = om.payment;

          if (om.minOrder) store.state.Order.order.minOrder = om.minOrder;
          else store.state.Order.order.minOrder = 0;

          if (om.minOrderForFreeDelivery)
            store.state.Order.order.minOrderForFreeDelivery = om.minOrderForFreeDelivery;
          else store.state.Order.order.minOrderForFreeDelivery = 0;

          if (om.priceLevel && Number.isInteger(om.priceLevel))
            store.state.Order.order.priceLevel = om.priceLevel;
          else store.state.Order.order.priceLevel = 0;
        }

        store.state.Order.order.paymentTips = mUtils.cloneObject(store.getters.paymentTips);
        if (!store.getters.doorDashEnabled) store.state.Order.order.doorDashDriveID = undefined;

        if (
          store.state.Menu.menu.length == 0 ||
          o_orderType != store.state.Order.order.orderType ||
          o_priceLevel != store.state.Order.order.priceLevel
        ) {
          mMenu
            .reloadMenu({
              orderType: store.state.Order.order.orderType,
              priceLevel: store.state.Order.order.priceLevel,
            })
            .then(() => {
              if (o_priceLevel != store.state.Order.order.priceLevel) {
                store.state.Order.order.orderItems = [];
              } else {
                let exItems = [];
                store.state.Order.order.orderItems.forEach(oi => {
                  if (
                    (oi.item.catOrderTypes && !oi.item.catOrderTypes.includes(orderType)) ||
                    (oi.item.orderTypes && !oi.item.orderTypes.includes(orderType)) ||
                    (oi.promo &&
                      (orderType == ORDER_TYPE.DINEIN ||
                        (oi.promo.CustomData &&
                          oi.promo.CustomData.OrderTypes &&
                          !oi.promo.CustomData.OrderTypes.includes(orderType))))
                  ) {
                    exItems.push(oi);
                  } else if (oi.bundleItems.length > 0) {
                    for (let bi of oi.bundleItems) {
                      if (!bi.orderItem.item.orderTypes.includes(orderType)) {
                        exItems.push(oi);
                        break;
                      }
                    }
                  }
                });

                if (exItems.length > 0) {
                  exItems.forEach(oi => {
                    this.removeOrderItem(oi);
                  });
                }
              }

              this.calculateTotal();

              resolve(true);
            });
        } else {
          resolve(true);
        }
      } catch (error) {
        console.log(error);
        resolve(false);
      }
    });
  },

  /**
   * Adds item to order and recalculates totals
   * @param {Object} orderItem - Item to add to order
   */
  addOrderItem(orderItem) {
    console.log("addOrderItem", orderItem);

    store.state.Order.order.orderItems.push(orderItem);
    this.linkCombo(orderItem);

    this.calculateTotal();

    console.log("orderItems", store.state.Order.order.orderItems);
  },

  /**
   * Removes item from order and recalculates totals
   * Also handles combo unlinking
   * @param {Object} orderItem - Item to remove from order
   */
  removeOrderItem(orderItem) {
    let linkedOrderItem = this.getLinkedOrderItem(orderItem);
    this.unlinkCombo(orderItem);

    const index = store.state.Order.order.orderItems.indexOf(orderItem);
    store.state.Order.order.orderItems.splice(index, 1);

    this.linkCombo(linkedOrderItem);

    if (store.state.Order.order.orderItems.length == 0) {
      store.state.Order.order.address = null;
      store.state.Order.order.deliveryQuoteValue = 0;
      store.state.Order.order.deliveryValue = 0;
      //store.state.Order.order.deliveryWithhold = 0;
    }

    this.calculateTotal();
  },

  /**
   * Finds combo-linked items
   * @param {Object} orderItem - Item to find linked combo for
   * @returns {Object|null} Linked combo item or null
   */
  getLinkedOrderItem(orderItem) {
    let linkedOrderItem = null;
    if (orderItem.cbRefID > 0) {
      for (let oi of store.state.Order.order.orderItems) {
        if (oi.ord_did != orderItem.ord_did && oi.cbRefID == orderItem.cbRefID) {
          linkedOrderItem = oi;
          break;
        }
      }
    }
    return linkedOrderItem;
  },

  /**
   * Links combo items based on tag matching
   * Handles price adjustments for combo items
   * @param {Object} orderItem - Item to link combos for
   * @returns {boolean} True if combo linked, false otherwise
   */
  linkCombo(orderItem) {
    if (orderItem == null) return false;
    if (orderItem.cbRefID > 0) return false;

    let linked = false;
    if (orderItem.tag != "") {
      let loopCount = orderItem.qty;
      for (let i = 0; i < loopCount; i++) {
        let combosFound = false;
        for (let oi of store.state.Order.order.orderItems) {
          if (oi.cbRefID == 0 && oi.combos && Array.isArray(oi.combos)) {
            for (let cb of oi.combos) {
              if (cb.tag == orderItem.tag && mStore.comboScheduleInRange(cb.ranges)) {
                if (oi.qty > 1) {
                  let cloneOi = mUtils.cloneObject(oi);
                  cloneOi.qty = cloneOi.qty - 1;
                  cloneOi.ord_did = store.state.Order.ordDidCount++;
                  store.state.Order.order.orderItems.push(cloneOi);

                  oi.qty = 1;
                }

                let cloneOrderItem;
                if (orderItem.qty == 1) cloneOrderItem = orderItem;
                else {
                  cloneOrderItem = mUtils.cloneObject(orderItem);
                  cloneOrderItem.qty = 1;
                  cloneOrderItem.ord_did = store.state.Order.ordDidCount++;

                  orderItem.qty--;

                  store.state.Order.order.orderItems.push(cloneOrderItem);
                }

                oi.combo = cb;

                if (cb.price != null) {
                  oi.price = cb.price;
                  if (cb.optionOntop && !cb.optionOntop) {
                    oi.optionValue = 0;
                  }
                } else if (cb.discountRate != null) {
                  oi.price = mUtils.roundNumber(oi.o_price * (1 - cb.discountRate / 100), 2);
                  let optionDiscountRate = cb.optionDiscountable ? cb.discountRate : 0;
                  oi.optionValue = mUtils.roundNumber(oi.o_optionValue * (1 - optionDiscountRate / 100), 2);
                }
                let cbRefID =
                  (store.state.Order.order.cbRefIDs.length == 0
                    ? 0
                    : Math.max(...store.state.Order.order.cbRefIDs)) + 1;

                oi.cbRefID = cbRefID;
                cloneOrderItem.cbRefID = cbRefID;

                combosFound = true;
                linked = true;

                if (cbRefID > 0) {
                  if (store.state.Order.order.cbRefIDs.indexOf(cbRefID) < 0) {
                    store.state.Order.order.cbRefIDs.push(cbRefID);
                  }
                }
                break;
              }
            }
          }
          if (combosFound) break;
        }

        if (!combosFound) {
          break;
        }
      }
    } else if (Array.isArray(orderItem.combos) && orderItem.combos.length > 0) {
      let loopCount = orderItem.qty;
      for (let i = 0; i < loopCount; i++) {
        let tagFound = false;
        for (let cb of orderItem.combos) {
          for (let oi of store.state.Order.order.orderItems) {
            if (oi.cbRefID == 0 && cb.tag == oi.tag && mStore.comboScheduleInRange(cb.ranges)) {
              if (oi.qty > 1) {
                let cloneOi = mUtils.cloneObject(oi);
                cloneOi.qty = cloneOi.qty - 1;
                cloneOi.ord_did = store.state.Order.ordDidCount++;
                store.state.Order.order.orderItems.push(cloneOi);

                oi.qty = 1;
              }

              let cloneOrderItem;
              if (orderItem.qty == 1) cloneOrderItem = orderItem;
              else {
                cloneOrderItem = mUtils.cloneObject(orderItem);
                cloneOrderItem.qty = 1;
                cloneOrderItem.ord_did = store.state.Order.ordDidCount++;

                orderItem.qty--;

                store.state.Order.order.orderItems.push(cloneOrderItem);
              }

              cloneOrderItem.combo = cb;

              if (cb.price != null) {
                cloneOrderItem.price = cb.price;
                if (cb.optionOntop && !cb.optionOntop) cloneOrderItem.optionValue = 0;
              } else if (cb.discountRate != null) {
                cloneOrderItem.price = mUtils.roundNumber(cloneOrderItem.o_price * (1 - cb.discountRate / 100), 2);
                if (cb.optionOntop) {
                  let optionDiscountRate = cb.optionDiscountable ? cb.discountRate : 0;
                  cloneOrderItem.optionValue = mUtils.roundNumber(
                    cloneOrderItem.o_optionValue * (1 - optionDiscountRate / 100),
                    2
                  );
                }
              }

              let cbRefID =
                (store.state.Order.order.cbRefIDs.length == 0
                  ? 0
                  : Math.max(...store.state.Order.order.cbRefIDs)) + 1;

              oi.cbRefID = cbRefID;
              cloneOrderItem.cbRefID = cbRefID;

              tagFound = true;
              linked = true;

              if (cbRefID > 0) {
                if (store.state.Order.order.cbRefIDs.indexOf(cbRefID) < 0) {
                  store.state.Order.order.cbRefIDs.push(cbRefID);
                }
              }
              break;
            }
          }
          if (tagFound) break;
        }

        if (!tagFound) {
          break;
        }
      }
    }

    // if (linked && cbRefID > 0) {
    //   if (store.state.Order.order.cbRefIDs.indexOf(cbRefID) < 0) {
    //     store.state.Order.order.cbRefIDs.push(cbRefID);
    //   }
    // }

    return linked;
  },

  /**
   * Unlinks combo items and resets prices
   * @param {Object} orderItem - Item to unlink combos from
   */
  unlinkCombo(orderItem) {
    //console.log(orderItem);

    let cbRefID = orderItem.cbRefID;
    let linkedOrderItem = this.getLinkedOrderItem(orderItem);
    if (linkedOrderItem) {
      if (Array.isArray(linkedOrderItem.combos) && linkedOrderItem.combos.length > 0) {
        linkedOrderItem.price = linkedOrderItem.o_price;
        linkedOrderItem.optionValue = linkedOrderItem.o_optionValue;
      }
      linkedOrderItem.cbRefID = 0;
    }
    orderItem.cbRefID = 0;

    let indexOfIDs = store.state.Order.order.cbRefIDs.indexOf(cbRefID);
    if (indexOfIDs >= 0) {
      store.state.Order.order.cbRefIDs.splice(indexOfIDs, 1);
    }
  },

  /**
   * 
   * classify 0 = food
   * classify 1 = bread
   * classify 2 = others
   * classify 3 = bar
   * classify 4 = beverage
   * classify 5 = package
   * classify 6 = comment
   * classify 7 = tea
   * classify 8 = taxembed (obsoleted)
   * classify 9 = headcount
   * classify 10 = discount
   * 
   */
  /**
   * Calculates order totals, taxes, and discounts
   * Handles complex tax rules including:
   * - GST/PST/Liquor tax calculations
   * - Tax rebates and exemptions
   * - Delivery/service charges
   * - Member discounts
   */
  calculateTotal() {
    if (store.state.Store.store == null) return;

    let gstRate = store.state.Store.store.info.gstRate / 100;
    let pstRate = store.state.Store.store.info.pstRate / 100;
    let liqRate = store.state.Store.store.info.liqRate / 100;
    let gstExt = 0;
    let pstExt = 0;
    let liqExt = 0;
    let dgstExt = 0;
    let dpstExt = 0;
    let dliqExt = 0;
    let gstTax = 0;
    let pstTax = 0;
    let liqTax = 0;
    let classify1Qty = 0;

    let deliveryGstRate = store.state.Store.store.info.deliveryGstRate / 100;
    let deliveryPstRate = store.state.Store.store.info.deliveryPstRate / 100;
    let serviceGstRate = store.state.Store.store.info.serviceGstRate / 100;
    let servicePstRate = store.state.Store.store.info.servicePstRate / 100;

    let billDiscountableTotal = 0;
    store.state.Order.order.qtyTotal = 0;
    store.state.Order.order.catQtyTotal = [];
    store.state.Order.order.redeemedPointsTotal = 0;
    store.state.Order.order.subtotal = 0;
    store.state.Order.order.tax = 0;
    store.state.Order.order.discountRate = 0;
    store.state.Order.order.discountValue = 0;
    store.state.Order.order.serviceRate = 0;
    store.state.Order.order.serviceValue = 0;

    //store.state.Order.orderedCounter = [];

    // get classify 1 count, order subtotal
    let itemsExt = 0;
    let classify0Ext = 0;
    let classify1Ext = 0;
    let classify4Ext = 0;
    for (let oi of store.state.Order.order.orderItems) {
      let classify = oi.item ? oi.item.classify : -1;
      let unit = oi.item?.unit > 0 ? oi.item.unit : 1;

      oi.extended = store.getters.orderItemExtended(oi);
      itemsExt += oi.extended;

      if (classify == 1) classify1Qty += (oi.qty * unit);

      if (classify == 0) classify0Ext += oi.extended;
      else if (classify == 1) classify1Ext += oi.extended;
      else if (classify == 4) classify4Ext += oi.extended;
    }

    let foodTaxRebate = store.state.Store.store.info.foodTaxRebate > 0 && itemsExt <= 4 ? store.state.Store.store.info.foodTaxRebate : 0;
    //console.log('foodTaxRebate', foodTaxRebate, itemsExt);
    for (let oi of store.state.Order.order.orderItems) {
      let classify = oi.item ? oi.item.classify : -1;

      let ext = oi.extended;
      store.state.Order.order.subtotal += ext;

      let bakeryTaxRebate = store.getters.storeAddress.province == "ON" && classify == 1 && classify1Qty >= 6;
      if (bakeryTaxRebate) {
        // 13% tax rebate on classify 1 items if order has 6 or more classify 1 items
      } else {
        if (oi.gst) {
          gstExt += ext;
          gstTax += ext * gstRate;

          dgstExt += oi.billDiscountable ? ext : 0;
        }
        if (oi.pst) {
          let applyPst = true;
          if (foodTaxRebate == 1) {
            if ((classify == 0 || classify == 1) && (classify0Ext + classify1Ext) <= 4) applyPst = false;
            else if (classify == 4 && classify4Ext <= 4) applyPst = false;
          } else if (foodTaxRebate == 2) {
            if ((classify == 0 || classify == 1 || classify == 4) &&
              (classify0Ext + classify1Ext > 0) && (classify4Ext > 0) && (classify0Ext + classify1Ext + classify4Ext) <= 4) applyPst = false;
          }
          //console.log('applyPst', applyPst, classify, classify0Ext, classify1Ext, classify4Ext);
          if (applyPst) {
            pstExt += ext;
            pstTax += ext * pstRate;

            dpstExt += oi.billDiscountable ? ext : 0;
          }
        }
        if (oi.liq) {
          liqExt += ext;
          liqTax += ext * liqRate;

          dliqExt += oi.billDiscountable ? ext : 0;
        }
      }

      store.state.Order.order.qtyTotal += oi.qty;
      if (oi.redeemItem) {
        store.state.Order.order.redeemedPointsTotal += oi.redeemPoints * oi.qty;
      }
      let cq = store.state.Order.order.catQtyTotal.find(c => c.catID == oi.item.catID);
      if (cq) {
        cq.total += oi.qty;
      } else {
        store.state.Order.order.catQtyTotal.push({ catID: oi.item.catID, total: oi.qty });
      }

      if (oi.billDiscountable) {
        billDiscountableTotal += ext;
      }
    }

    // get discount value - begin
    let memberDiscountRate = 0;
    let memberDiscountValue = 0;
    if (store.state.member) {
      if (store.state.member.MemberMetadata) {
        let memberMetadata = store.state.member.MemberMetadata;
        if (memberMetadata.MemberExDiscountRate)
          memberDiscountRate = Math.max(memberMetadata.MemberExDiscountRate, memberDiscountRate);
      }

      if (store.getters.memberValidVip) {
        if (store.state.member.VipCode) {
          if (store.state.member.VipDiscRate)
            memberDiscountRate = Math.max(store.state.member.VipDiscRate, memberDiscountRate);
          if (store.state.member.VipDiscRate1)
            memberDiscountValue = Math.max(store.state.member.VipDiscRate1, memberDiscountValue);
        }

        if (store.state.Store.store.info.vipDiscountRate)
          memberDiscountRate = Math.max(store.state.Store.store.info.vipDiscountRate, memberDiscountRate);
        if (store.state.Store.store.info.vipDiscountValue)
          memberDiscountValue = Math.max(store.state.Store.store.info.vipDiscountValue, memberDiscountValue);
      }

      if (isNaN(memberDiscountRate)) memberDiscountRate = 0;
      if (isNaN(memberDiscountValue)) memberDiscountValue = 0;
    }

    let orderDiscValue = 0;
    let orderDiscByPercentage = true;
    let promoDiscMinAmount = 0;
    let promoSubOi = store.state.Order.order.orderItems.find(oi => oi.promo);
    let promoSubOiExt = 0;
    if (promoSubOi) {
      if (
        promoSubOi.promo.TargetType == "subtotal" &&
        promoSubOi.promo.DiscValue &&
        typeof promoSubOi.promo.DiscValue == "number"
      ) {
        orderDiscValue = promoSubOi.promo.DiscValue;
        orderDiscByPercentage = promoSubOi.promo.DiscByPercentage === true;
      }

      if (promoSubOi.promo.MinAmount && typeof promoSubOi.promo.MinAmount == "number")
        promoDiscMinAmount = promoSubOi.promo.MinAmount;

      promoSubOiExt = promoSubOi.extended;
    }

    if (orderDiscValue == 0 || store.state.Order.order.subtotal - promoSubOiExt < promoDiscMinAmount) {
      if (memberDiscountRate > 0 || store.state.Order.order.subtotal > memberDiscountValue) {
        orderDiscByPercentage = true;
        orderDiscValue = Math.max(store.state.Order.order.defaultDiscountRate, memberDiscountRate);
      } else {
        orderDiscByPercentage = false;
        orderDiscValue = memberDiscountValue;
      }
    }

    if (orderDiscValue > 0) {
      if (orderDiscByPercentage) {
        store.state.Order.order.discountRate = orderDiscValue;
        store.state.Order.order.discountValue = billDiscountableTotal * (orderDiscValue / 100);
      } else {
        store.state.Order.order.discountValue = orderDiscValue;
      }
    }
    // get discount value - end

    if (store.state.Store.store.info.taxOnDiscountedSubtotal) {
      let gDisc = 0;
      let pDisc = 0;
      let lDisc = 0;
      if (store.state.Order.order.discountRate > 0) {
        gDisc = dgstExt * (store.state.Order.order.discountRate / 100);
        pDisc = dpstExt * (store.state.Order.order.discountRate / 100);
        lDisc = dliqExt * (store.state.Order.order.discountRate / 100);
      } else if (store.state.Order.order.discountValue > 0) {
        gDisc = dgstExt >= store.state.Order.order.discountValue ? store.state.Order.order.discountValue : 0;
        pDisc = dpstExt >= store.state.Order.order.discountValue ? store.state.Order.order.discountValue : 0;
        lDisc = dliqExt >= store.state.Order.order.discountValue ? store.state.Order.order.discountValue : 0;
      }

      gstTax = (gstExt - gDisc) * gstRate;
      pstTax = (pstExt - pDisc) * pstRate;
      liqTax = (liqExt - lDisc) * liqRate;
    }

    gstTax = mUtils.roundNumber(gstTax, 2);
    pstTax = mUtils.roundNumber(pstTax, 2);
    liqTax = mUtils.roundNumber(liqTax, 2);

    gstTax = gstTax < 0 ? 0 : gstTax;
    pstTax = pstTax < 0 ? 0 : pstTax;
    liqTax = liqTax < 0 ? 0 : liqTax;

    store.state.Order.order.tax = gstTax + pstTax + liqTax;

    // calculate delivery charge
    if (
      store.state.Order.order.orderItems.length == 0 ||
      store.state.Order.order.address == null ||
      (store.state.Order.order.minOrderForFreeDelivery > 0 &&
        store.state.Order.order.minOrderForFreeDelivery <=
        store.state.Order.order.subtotal - store.state.Order.order.discountValue)
    ) {
      store.state.Order.order.deliveryValue = 0;
    } else {
      store.state.Order.order.deliveryValue = store.state.Order.order.deliveryQuoteValue;

      if (store.getters.deliveryAgentEnabled) {
        store.state.Order.order.deliveryValue = store.getters.deliveryAgentAdjustedCharge(
          store.state.Order.order.deliveryValue
        );
      }
    }

    // calculate service charge
    let subtotalForService = store.state.Order.order.subtotal;
    if (store.state.Store.store.info.taxOnDiscountedSubtotal)
      subtotalForService -= store.state.Order.order.discountValue;

    if (store.state.Order.order.defaultServicePercentage) {
      store.state.Order.order.serviceRate = store.state.Order.order.defaultServiceValue;
      store.state.Order.order.serviceValue = subtotalForService * (store.state.Order.order.serviceRate / 100);
    } else {
      store.state.Order.order.serviceValue = store.state.Order.order.defaultServiceValue;
    }

    // calculate tax
    if (store.state.Order.order.deliveryValue != 0) {
      let deliveryTax = store.state.Order.order.deliveryValue * (deliveryGstRate + deliveryPstRate);
      store.state.Order.order.tax += deliveryTax;
    }
    if (store.state.Order.order.serviceValue != 0) {
      let serviceTax = store.state.Order.order.serviceValue * (serviceGstRate + servicePstRate);
      store.state.Order.order.tax += serviceTax;
    }

    store.state.Order.order.subtotal = mUtils.roundNumber(store.state.Order.order.subtotal, 2);
    store.state.Order.order.deliveryValue = mUtils.roundNumber(store.state.Order.order.deliveryValue, 2);
    store.state.Order.order.tax = mUtils.roundNumber(store.state.Order.order.tax, 2);
    store.state.Order.order.discountValue = mUtils.roundNumber(store.state.Order.order.discountValue, 2);
    store.state.Order.order.serviceValue = mUtils.roundNumber(store.state.Order.order.serviceValue, 2);

    store.state.Order.order.total =
      store.state.Order.order.subtotal +
      store.state.Order.order.deliveryValue +
      store.state.Order.order.serviceValue +
      store.state.Order.order.tax -
      store.state.Order.order.discountValue;
    store.state.Order.order.total = mUtils.roundNumber(store.state.Order.order.total, 2);

    this.setOrderToStorage();
  },

  /**
   * Adds side items to order when required by combo
   * @param {Object} orderItem - Main item requiring sides
   * @param {boolean} silent - Skip confirmation dialog
   */
  async addSideItem(orderItem, silent) {
    console.log('addSideItem', orderItem);
    if (!orderItem) return;

    let sideItemIDs = store.state.Menu.items
      .filter(
        x =>
          x.combos && x.combos.some(x1 => x1.tag == orderItem.tag && mStore.comboScheduleInRange(x1.ranges))
      )
      /*
      .sort(function(a, b) {
        return a.catID - b.catID;
      })
      */
      .map(y => y.ID);

    if (sideItemIDs.length > 0) {
      let adding = false;
      if (silent) {
        adding = true;
      } else {
        if (store.getters.addSideItemOptional) {
          let res = await mDialog.confirmDialog.open(i18n.t("confirm_add_side_item"), "", i18n.t("confirm_add_side_item_later"));
          if (res) adding = true;
        } else {
          let res = await mDialog.messageDialog.open(i18n.t("add_side_item"), "", i18n.t("add_side_item_ok"));
          if (res) adding = true;
        }
      }

      if (adding) {
        if (router.currentRoute.name == "MenuItem") {
          setTimeout(() => {
            mApp.navBack();
          }, 0);
        }

        setTimeout(() => {
          mApp.navName("MenuItem", {
            itemIDs: sideItemIDs,
            sideOrder: true,
          });
        }, 100);
      }
    }
  },

  /**
   * Updates order with current member/guest info
   * Clears redemption items for guests
   */
  updateOrderMember() {
    let order = store.state.Order.order;
    if (order.memberVip == null) {
      order.memberVip = store.getters.memberVip;
    }

    let member = store.state.member;
    if (member) {
      order.memberCode = store.getters.memberCode;
      order.memberVip = store.getters.memberValidVip;
      order.phoneID = store.getters.memberPhoneId;
      order.login = {
        memberCode: store.getters.memberCode,
        name: store.getters.memberFullName,
        firstName: member.MemberFirstName,
        lastName: member.MemberLastName,
        email: store.getters.memberEmail,
        phone1:
          store.getters.memberPhoneId != "" ? store.getters.memberPhoneId.substring(2) : store.getters.memberPhoneNo,
        phoneVerified: store.getters.memberPhoneId != "",
        isMember: true,
        isVip: store.getters.memberVip,
      };
    } else {
      order.memberCode = "";
      order.memberVip = false;
      order.phoneID = "";
      order.login = store.state.guest.phone1 == "" ? null : store.state.guest;

      order.orderItems = order.orderItems ? order.orderItems.filter(oi => !oi.redeemItem) : [];
      order.orderItems = order.orderItems ? order.orderItems.filter(oi => oi.promo == null) : [];
    }

    this.setOrderToStorage();
  },

  /**
   * Updates prices for all order items
   * Applies combo pricing rules
   */
  updateOrderItemsPrice() {
    let orderItems = store.state.Order.order.orderItems;

    if (orderItems) {
      orderItems.forEach(oi => {
        let item = oi.item;
        if (item) {
          oi.price = store.getters.itemPrice(item);
          oi.options.forEach(og => {
            og.ordered.forEach(opt => {
              let itemOpt = og.items.find(x => x.ID == opt.ID);
              if (itemOpt) {
                let price = store.getters.itemOptionPrice(itemOpt);
                opt.price = price;
                opt.optionPrice = price;
              }
            });
          });
          this.updateOptionsValue(oi);
        }
      });

      let sideItems = orderItems.filter(oi => oi.cbRefID > 0 && oi.combo && !mUtils.isEmptyObject(oi.combo));

      if (sideItems) {
        sideItems.forEach(si => {
          let cb = si.combo;

          if (cb.discountRate > 0) {
            si.price = mUtils.roundNumber(si.o_price * (1 - cb.discountRate / 100), 2);
            if (cb.optionOntop) {
              let optionDiscountRate = cb.optionDiscountable ? cb.discountRate : 0;
              si.optionValue = mUtils.roundNumber(si.o_optionValue * (1 - optionDiscountRate / 100), 2);
            }
          } else if (!isNaN(cb.price)) {
            si.price = cb.price;
            if (cb.optionOntop && !cb.optionOntop) si.optionValue = 0;
          }
        });
      }
    }
  },

  updateOptionsValue(oi) {
    let optValue = 0;

    let optDiscRate = oi.combo.optionDiscountable && oi.combo.optionDiscountable ? oi.combo.discountRate : 0;
    for (let og of oi.options) {
      if (og.ordered) {
        let counter = og.ordered.length;
        if (og.chgqty > 0) {
          if (og.chgway == 0) og.ordered.sort(this.sortOptionByTime);
          else if (og.chgway == 1) og.ordered.sort(this.sortOptionByPrice);
          else if (og.chgway == 2) og.ordered.sort(this.sortOptionByHighestPrice);

          counter = og.chgqty;
        }

        if (oi.combos == null || oi.cbRefID == 0 || oi.combo.optionOntop) {
          for (let opt of og.ordered) {
            let addOptPrice = true;
            if (og.chgqty > 0 && (oi.cbRefID == 0 || oi.combo.optionOntop)) {
              if (og.chgway == 0) addOptPrice = counter <= 0;
              else if (og.chgway == 1) addOptPrice = counter <= 0;
              else if (og.chgway == 2) addOptPrice = counter > 0;
            }

            if (addOptPrice) {
              //optValue += opt.optionPrice;
              opt.price = opt.optionPrice;
            } else {
              opt.price = 0;
            }

            optValue += opt.price;

            if (opt.optionPrice > 0) counter--;
          }
        }
      }
    }

    oi.o_optionValue = optValue;
    oi.optionValue = optValue * (1 - optDiscRate / 100.0);
  },

  sortOptionByTime(a, b) {
    if (a.time > b.time) return 1;
    else if (a.time < b.time) return -1;
    else return 0;
  },

  sortOptionByLatestTime(a, b) {
    if (a.time < b.time) return 1;
    else if (a.time > b.time) return -1;
    else return 0;
  },

  sortOptionByPrice(a, b) {
    if (a.price > b.price) return 1;
    else if (a.price < b.price) return -1;
    else return 0;
  },

  sortOptionByHighestPrice(a, b) {
    if (a.price < b.price) return 1;
    else if (a.price > b.price) return -1;
    else return 0;
  },

  sortOptionBySeq(a, b) {
    if (a.seq > b.seq) return 1;
    else if (a.seq < b.seq) return -1;
    else return 0;
  },

  generateReferenceID() {
    let str = "00000" + Math.floor(Math.random() * 100000).toString();
    let referenceID = str.substring(str.length - 5);

    return referenceID;
  },

  getOrderWithholdAmount() {
    let order = store.state.Order.order;

    let withholdAmount = 0;
    if (order.orderType == ORDER_TYPE.DELIVERY && store.getters.doorDashEnabled) {
      let deliveryGstRate = store.state.Store.store.info.deliveryGstRate / 100;
      let deliveryPstRate = store.state.Store.store.info.deliveryPstRate / 100;
      withholdAmount = mUtils.roundNumber(order.deliveryValue * (1 + deliveryGstRate + deliveryPstRate) + order.tips, 2);
    }

    return withholdAmount;
  },

  /**
   * Converts order to feed format for API submission
   * Handles complex transformations including:
   * - Combo item linking
   * - Promo data formatting
   * - Tax/discount calculations
   * @param {Object} [order] - Order to convert (uses current if null)
   * @returns {Object|null} Formatted order feed or null on error
   */
  orderToFeed(order) {
    try {
      if (order == null) order = store.state.Order.order;

      let preOrder = order.orderNow != true;
      let orderMemo = order.memo;
      let billMemo = "";
      if (store.getters.memoAsBillMemo) {
        billMemo = orderMemo;
        orderMemo = "";
      }
      if (
        (order.orderType == ORDER_TYPE.TAKEOUT || order.orderType == ORDER_TYPE.DELIVERY) &&
        store.getters.notAskUtensils === false
      ) {
        if ((store.getters.utensils === undefined || store.getters.utensils === true) && order.utensils !== true) {
          billMemo += (billMemo == "" ? "" : ", ") + i18n.t("no_utensils");
        } else if (
          (store.getters.utensils === undefined || store.getters.utensils === false) &&
          order.utensils === true
        ) {
          billMemo += (billMemo == "" ? "" : ", ") + i18n.t("need_utensils");
        }
      }

      if (preOrder) {
        billMemo += (billMemo == "" ? "" : ", ") + "Pre-Order";
      }

      let checkoutTime = mUtils.toSQLDateTimeString(order.checkoutTime);

      let pickupTimeIgnored = false;
      if (order.orderType == ORDER_TYPE.DELIVERY && store.getters.doorDashEnabled) {
        //pickupTimeIgnored = false;
      } else
        pickupTimeIgnored = !(
          !store.getters.checkoutPickupTimeIgnored &&
          (store.getters.alwaysSendPickupTime || !store.state.Order.order.orderNow)
        );

      let pickupTime = mUtils.toSQLDateTimeString(order.pickupTime);
      let deliveryTime = mUtils.toSQLDateTimeString(order.deliveryTime);

      let pickupAddress;
      if (store.getters.doorDashEnabled) {
        pickupAddress = mUtils.cloneObject(store.getters.storeAddress);
        if (store.getters.storeInfo) {
          pickupAddress.name = store.getters.storeInfo.companyName;
        }
      }

      let address = undefined;
      if (order.address) {
        address = mUtils.cloneObject(order.address);

        if (store.state.serverBuildNo <= 163) {
          if (pickupAddress && pickupAddress.unit && pickupAddress.street) {
            pickupAddress.street = "Unit " + pickupAddress.unit + " " + pickupAddress.street;
            pickupAddress.unit = "";
          }
          if (address && address.unit && address.street) {
            address.street = "Unit " + address.unit + " " + address.street;
            address.unit = "";
          }
        }
      }

      let orderBy = undefined;
      if (order.orderBy?.code) {
        orderBy = parseInt(order.orderBy.code);
        if (isNaN(orderBy)) orderBy = undefined;
      }

      let feed = {
        device: store.state.deviceInfo,
        orderUUID: order.orderUUID,
        orderType: order.orderType,
        orderBy: orderBy,
        tableGroupID: order.tableGroupID,
        tableID: order.tableID,
        tableName: order.tableName,
        phoneID: order.phoneID,
        memberCode: order.memberCode,
        guest: order.login,
        address: address,
        dineInAway: order.dineInAway,
        preOrder: preOrder,

        checkoutTimeTz: order.checkoutTime == null ? null : mUtils.toDate(order.checkoutTime).toISOString(),
        checkoutTime: checkoutTime,

        pickupTimeTz:
          pickupTime == null || order.pickupTime == null ? null : mUtils.toDate(order.pickupTime).toISOString(),
        pickupTime: pickupTime,
        pickupAddress: pickupAddress,
        pickupTimeIgnored: pickupTimeIgnored,

        deliveryTimeTz: order.deliveryTime == null ? null : mUtils.toDate(order.deliveryTime).toISOString(),
        deliveryTime: deliveryTime,
        //deliveryWithhold: order.deliveryWithhold,
        deliveryWithhold: this.getOrderWithholdAmount(),

        orderMemo: orderMemo,
        billMemo: billMemo,
        items: [],
        discountRate: order.discountRate,
        discountValue: order.discountValue,
        deliveryValue: order.deliveryValue,
        serviceRate: order.serviceRate,
        serviceValue: order.serviceValue,
        doorDashDriveID: order.doorDashDriveID,
        subtotal: order.subtotal,
        tax: order.tax,
        tips: order.tips,
        total: order.total,
        payAmount: order.payAmount,
        paid: [
          {
            amount: order.payAmount,
            currency: order.currency,
            data: order.payData,
          },
        ],
        //referenceID: referenceID,
        doorDash: order.doorDash,
        storeID: store.getters.storeID,
        storeName: store.getters.storeName,
        store: {
          ID: store.getters.storeID,
          name: store.getters.storeName,
          address: store.getters.storeAddress,
        },
        stationId: Vue.prototype.$appMode == APP_MODE.KIOSK ? localStorage.getItem("KIOSK_STATION_ID") : undefined,
        billPrinter: Vue.prototype.$appMode == APP_MODE.KIOSK ? localStorage.getItem("KIOSK_BILL_PRINTER") : undefined,
        app: store.getters.appInfo,
      };

      if (store.getters.orderOptionsSortBySeq) {
        for (let oi of order.orderItems) {
          for (let og of oi.options) {
            og.ordered.sort(this.sortOptionBySeq);
          }
        }
      }

      for (let oi of order.orderItems) {
        if (oi.cbRefID == 0 || oi.combos == null || oi.combos.length == 0) {
          let n_price = oi.price;
          if (oi.redeemItem) {
            n_price = oi.o_price;
          }

          let n_optionValue = oi.optionValue;
          let n_idiscValue = 0;
          let n_discountValue = 0;
          let n_redeemedPoints = 0;

          let n_options = [];
          if (oi.redeemItem) {
            n_redeemedPoints = oi.redeemPoints;
            n_idiscValue = oi.o_price;
            n_optionValue = 0;
            for (let og of oi.options) {
              for (let opt of og.ordered) {
                if (oi.redeemItem) {
                  n_optionValue += opt.price;
                  if (opt.redeemable) {
                    n_idiscValue += opt.price;
                  }
                }

                let n_opt = {
                  ID: opt.ID,
                  cgID: opt.cgID,
                  externalName: opt.descr,
                  price: opt.price,
                  key: opt.key,
                  paKey: og.paKey,
                };
                n_options.push(n_opt);
              }
            }
            n_discountValue = n_idiscValue * oi.qty;
          } else {
            for (let og of oi.options) {
              for (let opt of og.ordered) {
                let n_opt = {
                  ID: opt.ID,
                  cgID: opt.cgID,
                  externalName: opt.descr,
                  price: opt.price,
                  key: opt.key,
                  paKey: og.paKey,
                };
                n_options.push(n_opt);
              }
            }
          }

          let n_oi = {
            ID: oi.ID,
            ord_descr: oi.ord_descr,
            ord_descr1: oi.ord_descr1,
            ord_descr2: oi.ord_descr2,
            externalName: oi.descr,
            qty: oi.qty,
            price: n_price,
            extended: oi.extended,
            optionValue: n_optionValue,
            discountRate: !oi.redeemItem && oi.itemDiscountable ? oi.itemDiscountRate : 0,
            discountValue: n_discountValue,
            redeemedPoints: n_redeemedPoints,
            remark: oi.remark,
            cbRefID: oi.cbRefID,
            options: n_options,
            comboItem: null,
            bundleItems: [],
            bAdj: oi.bAdj,
            promoData: null,
            tag: oi.tag,
            countable: oi.item.countable ? oi.item.countable : null,
            scalable: oi.item.scalable ? oi.item.scalable : null,
            weight: oi.item.scalable ? oi.item.weight : null,
            weightUnit: oi.item.scalable ? oi.item.weightUnit : null,
          };
          if (oi.promo) {
            n_oi.promoData = {
              code: oi.promo.PromoteCode,
              title: oi.promo.Title,
              headline: oi.promo.Headline,
              remark: oi.promo.Remark,
              discValue: oi.promo.DiscValue,
              discByPercentage: oi.promo.DiscByPercentage,
              targetType: oi.promo.TargetType,
              minAmount: oi.promo.MinAmount,
            };
            n_oi.descr = oi.promo.Title;
            n_oi.billDiscountable = oi.billDiscountable;
            if (oi.promo.CustomData) {
              if (
                oi.promo.CustomData.Gst !== undefined ||
                oi.promo.CustomData.Pst !== undefined ||
                oi.promo.CustomData.Liq !== undefined
              ) {
                n_oi.metadata = {
                  gst: oi.promo.CustomData.Gst,
                  pst: oi.promo.CustomData.Pst,
                  liq: oi.promo.CustomData.Liq,
                };
              }
            }
          }

          if (store.state.serverBuildNo >= 143 && oi.bundleItems.length > 0) {
            for (let bi of oi.bundleItems) {
              let boi = bi.orderItem;
              let n_boi = {
                ID: boi.ID,
                ord_descr: boi.ord_descr,
                ord_descr1: boi.ord_descr1,
                ord_descr2: boi.ord_descr2,
                externalName: boi.descr,
                qty: boi.qty,
                price: boi.price,
                //extended: boi.extended,
                optionValue: boi.optionValue,
                discountValue: boi.itemDiscountValue,
                discountRate: boi.itemDiscountRate,
                remark: boi.remark,
                options: [],
                countable: boi.item.countable ? boi.item.countable : null,
              };
              for (let og of boi.options) {
                for (let opt of og.ordered) {
                  let n_opt = {
                    ID: opt.ID,
                    cgID: opt.cgID,
                    externalName: opt.descr,
                    price: opt.price,
                    key: opt.key,
                    paKey: og.paKey,
                  };
                  n_boi.options.push(n_opt);
                }
              }
              n_oi.bundleItems.push(n_boi);
            }
          }

          feed.items.push(n_oi);
        }
      }

      // embed combo item to item
      for (let oi of order.orderItems) {
        if (!(oi.cbRefID == 0 || oi.combos === undefined || oi.combos.length == 0)) {
          let n_oi = {
            ID: oi.ID,
            ord_descr: oi.ord_descr,
            ord_descr1: oi.ord_descr1,
            ord_descr2: oi.ord_descr2,
            ord_subtotal: oi.qty * (oi.price + oi.optionValue),
            externalName: oi.descr,
            qty: oi.qty,
            price: oi.o_price,
            extended: oi.extended,
            optionValue: oi.o_optionValue,
            remark: oi.remark,
            cbRefID: oi.cbRefID,
            options: [],
            countable: oi.item.countable ? oi.item.countable : null,
          };

          for (let og of oi.options) {
            for (let opt of og.ordered) {
              let n_opt = {
                ID: opt.ID,
                cgID: opt.cgID,
                externalName: opt.descr,
                price: opt.price,
                key: opt.key,
                paKey: og.paKey,
              };
              n_oi.options.push(n_opt);
            }
          }

          for (let moi of feed.items) {
            if (moi.cbRefID == oi.cbRefID) {
              moi.comboItem = n_oi;
            }
          }
        }
      }

      if (Vue.prototype.$devEnv) console.log("order", order);
      if (Vue.prototype.$devEnv) console.log("feed", feed);

      return feed;
    } catch (error) {
      console.log(error);

      return null;
    }
  },

  /**
   * Validates order via API before submission
   * @returns {Promise} API validation result
   */
  validateOrder() {
    let feed = this.orderToFeed();
    return mApp
      .apiPost(store.getters.doApiUrl + "/v2/validateorder", {
        sessionKey: store.state.Store.store?.sessionKey,
        feed: feed,
      })
      .then(data => {
        return data;
      });
  },

  verifyOrderPin(orderPin) {
    if (orderPin) {
      return mApp
        .apiPost(store.getters.doApiUrl + "/v2/verifyorderpin", {
          sessionKey: store.state.appSessionKey,
          orderPin: orderPin,
        })
        .then(data => {
          if (data && data.result == "ok") {
            return data.verified === true;
          } else return false;
        });
    } else return false;
  },

  /**
   * Persists order to localStorage
   */
  setOrderToStorage() {
    try {
      let key = "ORD_" + store.getters.storeCompanyCode;
      if (store.state.order == null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(store.state.order));
      }
    } catch (error) {
      console.log(error);
    }
  },

  /**
   * Retrieves order from localStorage
   * @returns {Object|null} Stored order or null
   */
  getOrderFromStorage() {
    let order = null;
    let key = "ORD_" + store.getters.storeCompanyCode;
    if (localStorage.getItem(key)) {
      order = JSON.parse(localStorage.getItem(key));
    }

    return order;
  },

  showNotAcceptOrderDialog() {
    let message = i18n.t("not_acception_order_message");

    if (store.state.storeHours) {
      if (store.state.storeHours.appData) {
        let _appData = store.state.storeHours.appData;
        if (_appData.expired === true) message = i18n.t("NOT_ACCEPT_ORDER.expired");
        else if (_appData.active !== true) message = i18n.t("NOT_ACCEPT_ORDER.not_active");
        else if (_appData.menuDisabled === true) message = i18n.t("NOT_ACCEPT_ORDER.menu_disabled");
      } else {
        if (store.state.storeHours.orderLimitExceed === true)
          message = i18n.t("NOT_ACCEPT_ORDER.exceed_order_limit") + "<br>" + message;
        else if (store.state.storeHours.orderHours.off === true) message = i18n.t("NOT_ACCEPT_ORDER.today_off") + "<br>" + message;
        else if (store.state.storeHours.orderHours.displayTimes)
          message =
            i18n.t("order_time", {
              orderTimes: mUtils.timesToString(store.state.storeHours.orderHours.displayTimes),
            }) +
            "<br>" +
            message;
      }
    }
    mDialog.messageDialog.open(i18n.t("not_acception_order"), "", message);
  },
};
